import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from "@angular/core";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { TranslateService } from "@ngx-translate/core";
import { NgxSpinnerService } from "ngx-spinner";
import { UserService } from "src/app/services/user.service";
import { SharedService } from "src/app/services/sharedService";
import { ProcessService } from "src/app/services/process.service";
import { ProcessDataSource } from "src/app/services/process.datasource";
import { MultiselectAutocompleteComponent } from "../../multiselect-autocomplete/multiselect-autocomplete.component";
import { CategoryService } from "src/app/services/category.service";
import { saveAs } from "file-saver";
import { merge } from "rxjs";
import { map, takeUntil, tap } from 'rxjs/operators';
import { RevenueCodeService } from "src/app/services/revenueCode.service";
import { BaseComponent } from "../../base.component";
import { ActivatedRoute } from "@angular/router";
import { FormTypes } from "src/app/util/formTypes";

@Component({
  selector: "rdf-stat-table-activity",
  templateUrl: "./stat-table-activity.component.html",
  styleUrls: ["./stat-table-activity.component.css"],
})
export class StatTableActivityComponent extends BaseComponent implements OnInit, AfterViewInit, OnDestroy {
  @Output() onFilter = new EventEmitter<any>();
  @Input() currentYear;
  @Input() municipality;
  @ViewChild("municipalitySelect", { static: false })
  municipalitySelect: MultiselectAutocompleteComponent;
  @ViewChild("revenueCodeSelect", { static: false })
  revenueCodeSelect: MultiselectAutocompleteComponent;
  @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: false }) sort: MatSort;
  singleMunicipality;
  dataSource: ProcessDataSource;
  filterObject: {};
  displayedColumns: string[] = ["name", "municipalityName", "revenueCode", "numberOfDeclarations", "numberOfStoppedDeclarations", "numberOfRemovedDeclarations", "numberOfUniqueUsers", "activeAvisesFee", "stoppedAvisesFee"];
  objectFilter = {};
  currentUser: any;
  municipalityChangeSubscription: any;
  activityState;
  searchRevenueCode: any
  formTypes = [FormTypes.YEARLY, FormTypes.MONTHLY, FormTypes.QUARTERLY];
  filterData = { municipality: null, revenueCode: null };
  declarationType: null;
  loading = { inProgress: false, error: false, downloadInProgress: false };
  selectedData = [];
  panelOpenState = true
  constructor(
    public translate: TranslateService,
    public userService: UserService,
    public sharedService: SharedService,
    public spinner: NgxSpinnerService,
    public processService: ProcessService,
    public categoryService: CategoryService,
    public revenueCodeService: RevenueCodeService,
    private route: ActivatedRoute,
  ) {
    super()
    this.userService.getCurrentUser().subscribe(user => {
      this.currentUser = user;
      this.dataSource = new ProcessDataSource(this.processService)
    })
  }

  resetLoading = () => {
    this.loading.inProgress = true;
    this.loading.error = false;
    this.dataSource.emptyData();
  };
  ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.municipalityChangeSubscription)
      this.municipalityChangeSubscription.unsubscribe();
  }
  async ngOnInit() {
    this.sharedService.setMunicipalitiesSubject$
      .pipe(takeUntil(this.destroy$))
      .subscribe((municipalities) => {
        if (municipalities) {
          this.filterData["municipality"] = municipalities.map((fld) => ({
            name: fld.name,
            id: fld.id,
          }));
        }
        setTimeout(() => {
          if (localStorage.getItem("municipality")) {
            if (localStorage.getItem("municipality") !== "-1") {
              this.municipalitySelect.resetAll();
              this.municipalitySelect.setSelectedData(
                this.filterData["municipality"].filter(
                  (elem) => elem.id === localStorage.getItem("municipality")
                )
              );
            } else {
              this.municipalitySelect.resetAll();
            }
          } else {
            this.municipalitySelect.resetAll();
          }
        }, 1000);
      });
    this.municipalityChangeSubscription =
      this.sharedService.changeMunicipalityEmitter.subscribe((val) => {
        if (localStorage.getItem("municipality")) {
          if (localStorage.getItem("municipality") !== "-1") {
            this.municipalitySelect.resetAll();
            this.municipalitySelect.setSelectedData(
              this.filterData["municipality"].filter(
                (elem) => elem.id === localStorage.getItem("municipality")
              )
            );
          } else {
            this.municipalitySelect.resetAll();
          }
        } else {
          this.municipalitySelect.resetAll();
        }
      });
    this.dataSource = new ProcessDataSource(this.processService)
    const revCodes = await this.revenueCodeService
      .getList("revenue-code")
      .toPromise();
    this.filterData["revenueCode"] = revCodes.records || revCodes;
    this.filterData.revenueCode.forEach((element) => {
      if (element.revenueCode && element.revenueCode.length > 0) {
        element.name = `${element.revenueCode[0].code} - ${element.revenueCode[0].name}`;
        element.id = element.revenueCode[0].id;
        element.code = element.revenueCode[0].code;
      } else {
        element.name = `${element.code} - ${element.name}`;
        element.code = element.code;
        element.id = element.id;
      }
    });
    this.filterData.revenueCode = this.filterData.revenueCode.filter(
      (e) => e.code
    );
    this.filterData.revenueCode.sort((a, b) => Number(a.code) - Number(b.code));
  }

  selectChange = (event: any, field) => {
    if (field === 'municipality') {
      this.municipality = event.data.map(elem => elem.id)
      if (this.municipality.length === 0) {
        this.municipality = null
      }
    }
    if (field === 'revenueCode') {
      this.searchRevenueCode = event.data.map(elem => elem.code)
      if (this.searchRevenueCode.length === 0) {
        this.searchRevenueCode = null
      }
    }
  };

  ngAfterViewInit() {
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));
    merge(this.sort.sortChange, this.paginator.page)
      .pipe(tap(() => this.loadProcessPage()))
      .subscribe();
  }

  async loadProcessPage() {
    this.spinner.show();
    this.objectFilter["year"] = [this.currentYear];
    this.dataSource.loadActivityList(
      this.sort.direction,
      this.sort.active,
      this.paginator.pageIndex,
      this.paginator.pageSize,
      this.objectFilter,
      this.spinner
    );
  }

  downloadStatisticsActivity() {
    let headers = [
      {
        header: this.translate.instant("STATISTICS.TABLE_COLUMN_NAME"),
        key: "name",
      },
      {
        header: this.translate.instant("STATISTICS.TABLE_COLUMN_MUNICIPALITY"),
        key: "municipality",
      },
      {
        header: this.translate.instant("STATISTICS.TABLE_COLUMN_REVENUE_CODE"),
        key: "revenueCode",
      },
      {
        header: this.translate.instant("STATISTICS.TABLE_COLUMN_NUMBER_OF_DECLARATIONS"),
        key: "numberOfDeclarations",
      },
      {
        header: this.translate.instant("STATISTICS.TABLE_COLUMN_NUMBER_OF_STOPPED_DECLARATIONS"),
        key: "numberOfStoppedDeclarations",
      },
      {
        header: this.translate.instant("STATISTICS.TABLE_COLUMN_NUMBER_OF_REMOVED_DECLARATIONS"),
        key: "numberOfRemovedDeclarations",
      },
      {
        header: this.translate.instant("STATISTICS.TABLE_COLUMN_NUMBER_OF_UNIQUE_USERS"),
        key: "numberOfUniqueUsers",
      },
      {
        header: this.translate.instant("STATISTICS.TABLE_COLUMN_ACTIVE_AVISES_FEE_AMOUNT"),
        key: "activeAvisesFee",
      },
      {
        header: this.translate.instant("STATISTICS.TABLE_COLUMN_STOPPED_AVISES_FEE_AMOUNT"),
        key: "stoppedAvisesFee",
      }
    ];
    this.spinner.show();
    this.processService
      .downloadActivityList(this.objectFilter, headers)
      .subscribe((data) => {
        this.spinner.hide();
        const blob = data;
        const file = new Blob([blob], {});
        const filename = "document_" + Date.now() + ".xlsx";
        saveAs(file, filename);
      });
  }

  advancedSearch() {
    if (this.declarationType == null) {
      delete this.objectFilter['type'];
    } else {
      this.objectFilter["type"] = [this.declarationType];
    }
    this.municipality && (this.objectFilter['municipality'] = this.municipality);
    this.searchRevenueCode && (this.objectFilter['searchRevenueCode'] = this.searchRevenueCode);
    let selData = []
    selData = this.municipalitySelect.selectData.filter(el => el.selected).map(el => el.item.id)
    if (localStorage.getItem('municipality')) {
      if (localStorage.getItem('municipality') == "-1") {
        delete this.objectFilter['municipality']
      } else {
        if (selData.length > 0)
          this.objectFilter['municipality'] = selData
        else
          this.objectFilter['municipality'] = [localStorage.getItem('municipality')]
      }
    } else {
      if (this.municipalitySelect.selectData.length === 0)
        delete this.objectFilter['municipality']
      if (this.revenueCodeSelect.selectData.length === 0)
        delete this.objectFilter['searchRevenueCode']
    }
    this.resetLoading()
    this.objectFilter["year"] = [this.currentYear];
    this.spinner.show();
    this.dataSource.loadActivityList(
      this.sort.direction,
      this.sort.active,
      this.paginator.pageIndex,
      this.paginator.pageSize,
      this.objectFilter,
      this.spinner
    );
    this.panelOpenState = false
  }

  resetFilter() {
    this.objectFilter = {}
    this.municipality = null
    this.searchRevenueCode = null
    this.declarationType = null
    this.municipalitySelect.resetAll()
    this.revenueCodeSelect.resetAll()
    this.dataSource['data'] = []
    this.dataSource.emptyData()
  }

  countTotal(field: string): number {
    if (!this.dataSource["data"]) return 0;
    return this.dataSource["data"].reduce(
      (sum, item) => sum + (item[field] || 0),
      0
    );
  }

  externalFunction() {
    this.loadProcessPage();
    this.dataSource.emptyData();
  }
}
